import type { TypedSupabaseClient } from '../client';
import type { TablesUpdate } from '../../../supabase-client/src/database.types';
import type {
  ISalesRepository,
  SalesDocument,
  SalesDocumentLine,
  SalesDocumentFilters,
  SalesDocType,
  CreateSalesDocumentInput,
  UpdateSalesDocumentInput,
} from '../../../interfaces/src';
import { findNextNumber, parseDocumentNumber } from '@erp/domain/numbering';
import { computePaymentStatus } from '@erp/domain/payments';
import { calculateSalesLineBenefit, netUnitCostHt } from '@erp/domain/sales';

const DOC_TYPE_PREFIX: Record<SalesDocType, string> = {
  quote: 'DEV',
  order: 'CMD',
  delivery: 'BL',
  invoice: 'FAC',
  credit_note: 'AV',
};

const DOC_TYPE_SEQUENCE: Record<SalesDocType, string> = {
  quote: 'seq_quote',
  order: 'seq_sales_order',
  delivery: 'seq_delivery',
  invoice: 'seq_invoice',
  credit_note: 'seq_credit_note',
};

const SALES_DOCUMENT_SELECT = `
  *,
  sales_document_lines(*)
`;

const SALES_DOCUMENT_LIST_SELECT = '*';

const PAYMENT_ALLOCATION_CHUNK_SIZE = 500;

export class SupabaseSalesRepository implements ISalesRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async listDocuments(filters?: SalesDocumentFilters): Promise<SalesDocument[]> {
    if (filters?.paymentStatus) return this.listDocumentsByPaymentStatus(filters);

    let query = (this.supabase as any).from('sales_documents').select(SALES_DOCUMENT_LIST_SELECT);
    query = applySalesDocumentFilters(query, filters);
    query = query.order('created_at', { ascending: false });
    if (filters?.limit && filters.limit > 0) {
      const offset = Math.max(0, filters.offset ?? 0);
      query = query.range(offset, offset + filters.limit - 1);
    }

    const { data, error } = await query;
    if (error) throw error;
    const docs = ((data ?? []) as Array<Record<string, unknown>>).map((row) => mapSalesDocument(row));
    return this.enrichDocumentsBatched(await this.attachLinesIfRequested(docs, filters));
  }

  async countDocuments(filters?: SalesDocumentFilters): Promise<number> {
    if (filters?.paymentStatus) return (await this.getPaymentStatusFilteredDocumentIds(filters)).length;

    let query = (this.supabase as any)
      .from('sales_documents')
      .select('id', { count: 'exact', head: true });
    query = applySalesDocumentFilters(query, filters);
    const { count, error } = await query;
    if (error) throw error;
    return count ?? 0;
  }

  private async listDocumentsByPaymentStatus(filters: SalesDocumentFilters): Promise<SalesDocument[]> {
    const matchingIds = await this.getPaymentStatusFilteredDocumentIds(filters);
    const offset = Math.max(0, filters.offset ?? 0);
    const pageIds = filters.limit && filters.limit > 0
      ? matchingIds.slice(offset, offset + filters.limit)
      : matchingIds.slice(offset);
    if (pageIds.length === 0) return [];

    const { data, error } = await (this.supabase as any)
      .from('sales_documents')
      .select(SALES_DOCUMENT_LIST_SELECT)
      .in('id', pageIds);
    if (error) throw error;

    const rank = new Map(pageIds.map((id, index) => [id, index]));
    const docs = ((data ?? []) as Array<Record<string, unknown>>)
      .map((row) => mapSalesDocument(row))
      .sort((a, b) => (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0));
    return this.enrichDocumentsBatched(await this.attachLinesIfRequested(docs, filters));
  }

  private async getPaymentStatusFilteredDocumentIds(filters: SalesDocumentFilters): Promise<string[]> {
    const requestedStatus = filters.paymentStatus;
    if (!requestedStatus) return [];

    let query = (this.supabase as any)
      .from('sales_documents')
      .select('id, doc_type, total_ttc, net_to_pay')
      .order('created_at', { ascending: false });
    query = applySalesDocumentFilters(query, filters);

    const { data, error } = await query;
    if (error) throw error;

    const candidates = (data ?? []) as Array<Record<string, unknown>>;
    if (candidates.length === 0) return [];

    const allocatedByDocumentId = await this.getPaymentAllocationSums(candidates);
    return candidates
      .filter((doc) => {
        const id = doc['id'] as string;
        const totalDue = Number(doc['net_to_pay'] ?? doc['total_ttc'] ?? 0);
        return computePaymentStatus(totalDue, allocatedByDocumentId.get(id) ?? 0) === requestedStatus;
      })
      .map((doc) => doc['id'] as string);
  }

  private async getPaymentAllocationSums(documents: Array<Record<string, unknown>>): Promise<Map<string, number>> {
    const sums = new Map<string, number>();
    const documentTypes = Array.from(new Set(documents.map((doc) => doc['doc_type']).filter(Boolean))) as string[];
    const ids = documents.map((doc) => doc['id']).filter(Boolean) as string[];

    for (let i = 0; i < ids.length; i += PAYMENT_ALLOCATION_CHUNK_SIZE) {
      const chunk = ids.slice(i, i + PAYMENT_ALLOCATION_CHUNK_SIZE);
      let query = (this.supabase as any)
        .from('payment_allocations')
        .select('document_id, document_type, allocated_amount')
        .in('document_id', chunk);
      if (documentTypes.length > 0) query = query.in('document_type', documentTypes);

      const { data, error } = await query;
      if (error) throw error;

      for (const allocation of (data ?? []) as Array<Record<string, unknown>>) {
        const documentId = allocation['document_id'] as string;
        const amount = Number(allocation['allocated_amount'] ?? 0);
        sums.set(documentId, (sums.get(documentId) ?? 0) + amount);
      }
    }

    return sums;
  }

  private async attachLinesIfRequested(
    docs: SalesDocument[],
    filters?: SalesDocumentFilters,
  ): Promise<SalesDocument[]> {
    if (!filters?.includeLines || docs.length === 0) return docs;

    const ids = docs.map((doc) => doc.id);
    const { data, error } = await (this.supabase as any)
      .from('sales_document_lines')
      .select('*')
      .in('document_id', ids)
      .order('line_number', { ascending: true });
    if (error) throw error;

    const linesByDocId = new Map<string, SalesDocumentLine[]>();
    for (const row of (data ?? []) as Array<Record<string, unknown>>) {
      const line = mapSalesLine(row);
      const lines = linesByDocId.get(line.documentId) ?? [];
      lines.push(line);
      linesByDocId.set(line.documentId, lines);
    }

    return docs.map((doc) => ({ ...doc, lines: linesByDocId.get(doc.id) ?? [] }));
  }

  async getDocumentById(id: string): Promise<SalesDocument | null> {
    const { data, error } = await (this.supabase as any)
      .from('sales_documents')
      .select(SALES_DOCUMENT_SELECT)
      .eq('id', id)
      .single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return this.enrichDocumentCustomer(mapSalesDocument(data));
  }

  async createDocument(input: CreateSalesDocumentInput): Promise<SalesDocument> {
    const number = input.number?.trim() || await this.getNextNumber(input.docType);
    const customerSource = input.customerSource
      ?? (input.proCustomerId ? 'pro' : input.onlineCustomerId ? 'online' : 'local');

    const insertPayload = {
      doc_type: input.docType,
      number,
      customer_source: customerSource,
      client_id: customerSource === 'local' ? input.clientId : null,
      online_customer_id: customerSource === 'online' ? input.onlineCustomerId ?? null : null,
      pro_customer_id: customerSource === 'pro' ? input.proCustomerId ?? null : null,
      pro_client_id: customerSource === 'pro' ? input.proClientId ?? null : null,
      representative_id: input.representativeId ?? null,
      car_registration_number: input.carRegistrationNumber ?? null,
      doc_date: input.docDate,
      validity_date: input.validityDate,
      parent_doc_id: input.parentDocId,
      parent_doc_ids: input.parentDocIds ?? (input.parentDocId ? [input.parentDocId] : []),
      pro_commande_code: input.proCommandeCode ?? null,
      notes: input.notes,
      total_ht: input.totalHt ?? 0,
      total_discount: input.totalDiscount ?? 0,
      total_vat: input.totalVat ?? 0,
      total_ttc: input.totalTtc ?? 0,
      withholding_amount: input.withholdingAmount ?? 0,
      stamp_amount: input.stampAmount ?? 0,
      // Cast: delivery_fee was added by migration 20260425300000 but isn't in
      // the generated database.types.ts yet. Run `supabase gen types typescript`
      // to refresh and drop this cast.
      delivery_fee: input.deliveryFee ?? 0,
      net_to_pay: input.netToPay ?? 0,
    };
    const { data: doc, error: docError } = await this.supabase
      .from('sales_documents')
      .insert(insertPayload as never)
      .select()
      .single();
    if (docError) throw docError;

    if (input.lines.length > 0) {
      const netCosts = await resolveNetUnitCosts(this.supabase, input.lines);
      const lines = input.lines.map((line, idx) => ({
        document_id: doc.id,
        line_number: idx + 1,
        article_id: line.articleId,
        description: line.description,
        quantity: line.quantity,
        unit_price_ht: line.unitPriceHt,
        purchase_price: netCosts[idx] ?? 0,
        discount_pct: line.discountPct ?? 0,
        vat_rate: line.vatRate,
        total_ht: line.totalHt ?? 0,
        total_ttc: line.totalTtc ?? 0,
      }));

      const { error: linesError } = await this.supabase.from('sales_document_lines').insert(lines);
      if (linesError) throw linesError;
    }

    const fresh = (await this.getDocumentById(doc.id))!;
    await this.syncLinkedStorefrontOrderAndDelivery(fresh);
    return (await this.getDocumentById(doc.id))!;
  }

  async updateDocument(id: string, input: UpdateSalesDocumentInput): Promise<SalesDocument> {
    const mapped: TablesUpdate<'sales_documents'> = {};
    if (input.customerSource !== undefined) (mapped as Record<string, unknown>)['customer_source'] = input.customerSource;
    if (input.clientId !== undefined) mapped['client_id'] = input.clientId;
    if (input.onlineCustomerId !== undefined) (mapped as Record<string, unknown>)['online_customer_id'] = input.onlineCustomerId;
    if (input.proCustomerId !== undefined) (mapped as Record<string, unknown>)['pro_customer_id'] = input.proCustomerId;
    if (input.proClientId !== undefined) (mapped as Record<string, unknown>)['pro_client_id'] = input.proClientId;
    if (input.representativeId !== undefined) mapped['representative_id'] = input.representativeId;
    if (input.carRegistrationNumber !== undefined) (mapped as Record<string, unknown>)['car_registration_number'] = input.carRegistrationNumber;
    if (input.docDate !== undefined) mapped['doc_date'] = input.docDate;
    if (input.validityDate !== undefined) mapped['validity_date'] = input.validityDate;
    if (input.notes !== undefined) mapped['notes'] = input.notes;
    if (input.totalHt !== undefined) mapped['total_ht'] = input.totalHt;
    if (input.totalDiscount !== undefined) mapped['total_discount'] = input.totalDiscount;
    if (input.totalVat !== undefined) mapped['total_vat'] = input.totalVat;
    if (input.totalTtc !== undefined) mapped['total_ttc'] = input.totalTtc;
    if (input.withholdingAmount !== undefined) mapped['withholding_amount'] = input.withholdingAmount;
    if (input.stampAmount !== undefined) mapped['stamp_amount'] = input.stampAmount;
    if (input.deliveryFee !== undefined) (mapped as Record<string, unknown>)['delivery_fee'] = input.deliveryFee;
    if (input.netToPay !== undefined) mapped['net_to_pay'] = input.netToPay;
    if (input.storefrontOrderStatus !== undefined) {
      (mapped as Record<string, unknown>)['storefront_order_status'] = input.storefrontOrderStatus;
    }
    if (input.proCommandeCode !== undefined) {
      (mapped as Record<string, unknown>)['pro_commande_code'] = input.proCommandeCode;
    }

    if (Object.keys(mapped).length > 0) {
      const { error } = await this.supabase.from('sales_documents').update(mapped).eq('id', id);
      if (error) throw error;
    }

    // Replace lines if provided
    if (input.lines) {
      // Delete old lines
      const { error: delError } = await this.supabase
        .from('sales_document_lines')
        .delete()
        .eq('document_id', id);
      if (delError) throw delError;

      // Insert new lines
      if (input.lines.length > 0) {
        const netCosts = await resolveNetUnitCosts(this.supabase, input.lines);
        const lines = input.lines.map((line, idx) => ({
          document_id: id,
          line_number: idx + 1,
          article_id: line.articleId,
          description: line.description,
          quantity: line.quantity,
          unit_price_ht: line.unitPriceHt,
          purchase_price: netCosts[idx] ?? 0,
          discount_pct: line.discountPct ?? 0,
          vat_rate: line.vatRate,
          total_ht: line.totalHt ?? 0,
          total_ttc: line.totalTtc ?? 0,
        }));
        const { error: insError } = await this.supabase.from('sales_document_lines').insert(lines);
        if (insError) throw insError;
      }
    }

    const fresh = (await this.getDocumentById(id))!;
    await this.syncLinkedProCommandeFromDelivery(fresh);
    await this.syncLinkedStorefrontOrderAndDelivery(fresh);
    return fresh;
  }

  async deleteDocument(id: string): Promise<void> {
    // Look up the doc being deleted so we can revert parent flags after.
    const { data: doc, error: fetchErr } = await this.supabase
      .from('sales_documents')
      .select('id, doc_type, parent_doc_id, parent_doc_ids')
      .eq('id', id)
      .single();
    if (fetchErr) throw fetchErr;

    // Collect parent IDs whose is_invoiced flag may need resetting.
    // Only invoices flip this flag on parents (deliveries/orders), so only
    // invoice deletion can revert it.
    const parentIds: string[] = [];
    if (doc?.['doc_type'] === 'invoice') {
      const single = doc['parent_doc_id'] as string | null;
      const many = (doc['parent_doc_ids'] as string[] | null) ?? [];
      if (single) parentIds.push(single);
      for (const pid of many) if (pid && !parentIds.includes(pid)) parentIds.push(pid);
    }

    const { error: delErr } = await this.supabase.from('sales_documents').delete().eq('id', id);
    if (delErr) throw delErr;

    // For each parent, only revert is_invoiced if no OTHER invoice still references it.
    for (const pid of parentIds) {
      const { data: stillReferenced } = await this.supabase
        .from('sales_documents')
        .select('id')
        .eq('doc_type', 'invoice')
        .or(`parent_doc_id.eq.${pid},parent_doc_ids.cs.{${pid}}`)
        .limit(1);
      if (!stillReferenced || stillReferenced.length === 0) {
        await this.supabase
          .from('sales_documents')
          .update({ is_invoiced: false })
          .eq('id', pid);
      }
    }
  }

  async getNextNumber(docType: SalesDocType): Promise<string> {
    const prefix = DOC_TYPE_PREFIX[docType];
    const seqName = DOC_TYPE_SEQUENCE[docType];

    const year = new Date().getFullYear();
    const readExistingValues = async (): Promise<number[]> => {
      const { data: rows } = await this.supabase
        .from('sales_documents')
        .select('number')
        .eq('doc_type', docType)
        .like('number', `${prefix}-${year}-%`);

      return (rows ?? [])
        .map((r) => parseDocumentNumber(r['number'] as string))
        .filter((p): p is NonNullable<typeof p> => p !== null && p.year === year && p.prefix === prefix)
        .map((p) => p.value);
    };

    // Lit le réglage de réutilisation des numéros supprimés.
    // Filter explicitly by current_user_tenant_id() — for platform
    // admins RLS exposes >1 tenant's settings rows; we want THIS
    // tenant's flag, not whichever Postgres returns first.
    type SbAny = { rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: string | null; error: { message: string } | null }> };
    const { data: tenantId } = await (this.supabase as unknown as SbAny).rpc(
      'current_user_tenant_id',
      {},
    );
    const { data: settingsRow } = await this.supabase
      .from('company_settings')
      .select('reuse_deleted_numbers')
      .eq('tenant_id', tenantId ?? '')
      .maybeSingle();
    const reuseDeleted = Boolean(settingsRow?.['reuse_deleted_numbers']);

    if (reuseDeleted) {
      // Mode réutilisation : on se base sur les numéros réellement existants
      const existingValues = await readExistingValues();

      const { value } = findNextNumber(existingValues, 0, true);
      // Maintient la séquence Postgres alignée sur le max observé pour éviter des
      // collisions si l'utilisateur désactive plus tard la réutilisation.
      const newMax = Math.max(value, ...existingValues);
      await this.supabase.rpc('set_sequence_value', { seq_name: seqName, new_value: newMax });
      return `${prefix}-${year}-${String(value).padStart(4, '0')}`;
    }

    // Mode strict (par défaut) : séquence atomique côté Postgres
    const { data, error } = await this.supabase.rpc('get_next_sequence_value', { seq_name: seqName });
    if (!error && data != null) {
      const sequenceValue = Number(data);
      const existingValues = await readExistingValues();
      const maxExisting = existingValues.length === 0 ? 0 : Math.max(...existingValues);
      let value = sequenceValue;

      if (sequenceValue <= maxExisting) {
        await this.supabase.rpc('set_sequence_value', { seq_name: seqName, new_value: maxExisting });
        const { data: syncedData, error: syncedError } = await this.supabase.rpc(
          'get_next_sequence_value',
          { seq_name: seqName },
        );
        value = !syncedError && syncedData != null ? Number(syncedData) : maxExisting + 1;
      }

      return `${prefix}-${year}-${String(value).padStart(4, '0')}`;
    }

    // Fallback: derive from the highest existing number instead of row count,
    // because deleted rows or imported historical docs can make count+1 collide.
    const existingValues = await readExistingValues();
    const next = existingValues.length === 0 ? 1 : Math.max(...existingValues) + 1;
    await this.supabase.rpc('set_sequence_value', { seq_name: seqName, new_value: next });
    return `${prefix}-${year}-${String(next).padStart(4, '0')}`;
  }

  async markAsInvoiced(id: string): Promise<void> {
    const { error } = await this.supabase
      .from('sales_documents')
      .update({ is_invoiced: true })
      .eq('id', id);
    if (error) throw error;
  }

  async getChildDocuments(parentId: string): Promise<SalesDocument[]> {
    const { data, error } = await this.supabase
      .from('sales_documents')
      .select('*, sales_document_lines(*)')
      .or(`parent_doc_id.eq.${parentId},parent_doc_ids.cs.{${parentId}}`)
      .order('created_at', { ascending: false });
    if (error) throw error;
    const docs = (data ?? []).map(mapSalesDocument);
    return this.enrichDocumentsBatched(docs);
  }

  // Single-doc enrichment fires 1–2 sub-queries. Mapping it over a list of N
  // docs via Promise.all (the old pattern) issued up to 2N parallel Supabase
  // requests per call — and the prewarm tick calls listDocuments every 5 min
  // with up to 500 rows per page. That stampede was overwhelming PostgREST
  // and surfacing as intermittent 500s on /rest/v1/sales_documents.
  // Collect every distinct customer id across the batch and resolve all
  // related rows in at most four `.in('id', [...])` queries.
  private async enrichDocumentsBatched(docs: SalesDocument[]): Promise<SalesDocument[]> {
    if (docs.length === 0) return docs;

    const collect = (pick: (d: SalesDocument) => string | null | undefined): string[] => {
      const ids = new Set<string>();
      for (const d of docs) {
        const v = pick(d);
        if (v) ids.add(v);
      }
      return [...ids];
    };

    const clientIds = collect((d) => d.clientId);
    const onlineCustomerIds = collect((d) => d.onlineCustomerId ?? null);
    const proCustomerIds = collect((d) => d.proCustomerId ?? null);
    const proClientIds = collect((d) => d.proClientId ?? null);

    type IdRows = { data: Array<Record<string, unknown>> | null; error: { message: string } | null };
    const empty: IdRows = { data: [], error: null };
    const fetchIn = (table: string, ids: string[]): Promise<IdRows> => {
      if (ids.length === 0) return Promise.resolve(empty);
      return (this.supabase as any).from(table).select('*').in('id', ids) as Promise<IdRows>;
    };

    const [clientsRes, onlineRes, proCustomerRes, proClientRes] = await Promise.all([
      fetchIn('clients', clientIds),
      fetchIn('online_customers', onlineCustomerIds),
      fetchIn('pro_customers', proCustomerIds),
      fetchIn('pro_clients', proClientIds),
    ]);

    if (clientsRes.error) throw clientsRes.error;
    if (onlineRes.error) throw onlineRes.error;
    if (proCustomerRes.error) throw proCustomerRes.error;
    if (proClientRes.error) throw proClientRes.error;

    const toMap = <T>(rows: Array<Record<string, unknown>> | null, mapFn: (r: Record<string, unknown>) => T): Map<string, T> => {
      const m = new Map<string, T>();
      for (const r of rows ?? []) m.set(r['id'] as string, mapFn(r));
      return m;
    };
    const clientMap = toMap(clientsRes.data, mapClient);
    const onlineMap = toMap(onlineRes.data, mapOnlineCustomer);
    const proCustomerMap = toMap(proCustomerRes.data, mapProCustomer);
    const proClientMap = toMap(proClientRes.data, mapProClient);

    return docs.map((doc) => {
      if (doc.customerSource === 'pro' || doc.proCustomerId) {
        return {
          ...doc,
          proCustomer: doc.proCustomerId ? proCustomerMap.get(doc.proCustomerId) ?? null : null,
          proClient: doc.proClientId ? proClientMap.get(doc.proClientId) ?? null : null,
        };
      }
      if (doc.customerSource === 'online' || doc.onlineCustomerId) {
        return {
          ...doc,
          onlineCustomer: doc.onlineCustomerId ? onlineMap.get(doc.onlineCustomerId) ?? null : null,
        };
      }
      if (doc.clientId) {
        return { ...doc, client: clientMap.get(doc.clientId) };
      }
      return doc;
    });
  }

  private async enrichDocumentCustomer(doc: SalesDocument): Promise<SalesDocument> {
    if (doc.customerSource === 'pro' || doc.proCustomerId) {
      const [proCustomer, proClient] = await Promise.all([
        doc.proCustomerId
          ? (this.supabase as any)
            .from('pro_customers')
            .select('*')
            .eq('id', doc.proCustomerId)
            .maybeSingle()
          : Promise.resolve({ data: null, error: null }),
        doc.proClientId
          ? (this.supabase as any)
            .from('pro_clients')
            .select('*')
            .eq('id', doc.proClientId)
            .maybeSingle()
          : Promise.resolve({ data: null, error: null }),
      ]);
      if (proCustomer.error) throw proCustomer.error;
      if (proClient.error) throw proClient.error;
      return {
        ...doc,
        proCustomer: proCustomer.data ? mapProCustomer(proCustomer.data) : null,
        proClient: proClient.data ? mapProClient(proClient.data) : null,
      };
    }

    if (doc.customerSource === 'online' || doc.onlineCustomerId) {
      const { data, error } = doc.onlineCustomerId
        ? await (this.supabase as any)
          .from('online_customers')
          .select('*')
          .eq('id', doc.onlineCustomerId)
          .maybeSingle()
        : { data: null, error: null };
      if (error) throw error;
      return { ...doc, onlineCustomer: data ? mapOnlineCustomer(data) : null };
    }

    if (doc.clientId) {
      const { data, error } = await this.supabase
        .from('clients')
        .select('*')
        .eq('id', doc.clientId)
        .maybeSingle();
      if (error) throw error;
      return { ...doc, client: data ? mapClient(data) : undefined };
    }

    return doc;
  }

  private async syncLinkedStorefrontOrderAndDelivery(doc: SalesDocument): Promise<void> {
    if (this.isStorefrontOrder(doc)) {
      const delivery = await this.getLatestChildDelivery(doc.id);
      if (!delivery) return;
      await this.overwriteLinkedStorefrontDocument(delivery, doc);
      return;
    }

    if (doc.docType !== 'delivery') return;
    const parentOrder = await this.getStorefrontParentOrder(doc);
    if (!parentOrder) return;
    await this.overwriteLinkedStorefrontDocument(parentOrder, doc);
  }

  private isStorefrontOrder(doc: SalesDocument): boolean {
    return doc.docType === 'order' && (doc.channel === 'storefront' || Boolean(doc.onlineCustomerId));
  }

  private async getLatestChildDelivery(orderId: string): Promise<SalesDocument | null> {
    const { data, error } = await this.supabase
      .from('sales_documents')
      .select('id')
      .eq('doc_type', 'delivery')
      .or(`parent_doc_id.eq.${orderId},parent_doc_ids.cs.{${orderId}}`)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    const deliveryId = (data?.['id'] as string | undefined) ?? null;
    return deliveryId ? this.getDocumentById(deliveryId) : null;
  }

  private async getStorefrontParentOrder(delivery: SalesDocument): Promise<SalesDocument | null> {
    const parentIds = delivery.parentDocIds?.length
      ? delivery.parentDocIds
      : delivery.parentDocId
        ? [delivery.parentDocId]
        : [];
    if (parentIds.length === 0) return null;

    const { data, error } = await this.supabase
      .from('sales_documents')
      .select('id')
      .eq('doc_type', 'order')
      .in('id', parentIds);
    if (error) throw error;

    for (const row of data ?? []) {
      const order = await this.getDocumentById(row['id'] as string);
      if (order && this.isStorefrontOrder(order)) return order;
    }
    return null;
  }

  private async overwriteLinkedStorefrontDocument(target: SalesDocument, source: SalesDocument): Promise<void> {
    const payload: Record<string, unknown> = {
      customer_source: source.customerSource,
      client_id: source.clientId || null,
      online_customer_id: source.onlineCustomerId ?? null,
      pro_customer_id: source.proCustomerId ?? null,
      pro_client_id: source.proClientId ?? null,
      representative_id: source.representativeId ?? null,
      car_registration_number: source.carRegistrationNumber ?? null,
      doc_date: source.docDate,
      validity_date: source.validityDate,
      notes: source.notes,
      total_ht: source.totalHt,
      total_discount: source.totalDiscount,
      total_vat: source.totalVat,
      total_ttc: source.totalTtc,
      withholding_amount: source.withholdingAmount,
      stamp_amount: source.stampAmount,
      delivery_fee: source.deliveryFee,
      net_to_pay: source.netToPay,
    };

    if (target.docType === 'delivery' && source.channel) {
      payload['channel'] = source.channel;
    }

    const { error: updateError } = await this.supabase
      .from('sales_documents')
      .update(payload)
      .eq('id', target.id);
    if (updateError) throw updateError;

    const { error: deleteError } = await this.supabase
      .from('sales_document_lines')
      .delete()
      .eq('document_id', target.id);
    if (deleteError) throw deleteError;

    const lines = (source.lines ?? []).map((line, idx) => ({
      document_id: target.id,
      line_number: idx + 1,
      article_id: line.articleId,
      description: line.description,
      quantity: line.quantity,
      unit_price_ht: line.unitPriceHt,
      purchase_price: line.purchasePrice ?? 0,
      discount_pct: line.discountPct ?? 0,
      vat_rate: line.vatRate,
      total_ht: line.totalHt ?? 0,
      total_ttc: line.totalTtc ?? 0,
    }));
    if (lines.length === 0) return;

    const { error: insertError } = await this.supabase.from('sales_document_lines').insert(lines);
    if (insertError) throw insertError;
  }

  private async syncLinkedProCommandeFromDelivery(doc: SalesDocument): Promise<void> {
    if (doc.docType !== 'delivery' || !doc.proCommandeCode) return;

    const { data: commande, error: commandeError } = await (this.supabase as any)
      .from('pro_commandes')
      .select('code, tenant_id, pro_customer_id, total_used')
      .eq('code', doc.proCommandeCode)
      .maybeSingle();
    if (commandeError) throw commandeError;
    if (!commande) return;

    const lines = doc.lines ?? [];
    const missingArticle = lines.find((line) => !line.articleId);
    if (missingArticle) {
      throw new Error('Les lignes du BL lie a une commande Pro doivent garder un article.');
    }

    const articleIds = [...new Set(lines.map((line) => line.articleId).filter(Boolean))] as string[];
    const { data: articles, error: articlesError } = articleIds.length > 0
      ? await (this.supabase as any)
        .from('articles')
        .select('id, purchase_price_ht, supplier_discount')
        .in('id', articleIds)
      : { data: [], error: null };
    if (articlesError) throw articlesError;
    const netCostByArticle = new Map<string, number>();
    for (const row of (articles ?? []) as Array<Record<string, unknown>>) {
      const net = netUnitCostHt({
        purchasePriceHt: Number(row['purchase_price_ht'] ?? 0),
        supplierDiscount: Number(row['supplier_discount'] ?? 0),
      });
      if (net != null) netCostByArticle.set(row['id'] as string, net);
    }

    const { data: proCustomer, error: proCustomerError } = await (this.supabase as any)
      .from('pro_customers')
      .select('commission_rate')
      .eq('id', commande.pro_customer_id)
      .maybeSingle();
    if (proCustomerError) throw proCustomerError;
    const commissionRate = Number(proCustomer?.commission_rate ?? 0);

    // Commission = marge positive × taux. Marge = bénéfice ligne (source unique
    // @erp/domain/sales) : prix de vente HT remisé − coût net HT (snapshot de la
    // ligne, sinon catalogue net de remise fournisseur).
    const totalCommission = lines.reduce((sum, line) => {
      if (!line.articleId) return sum;
      const snapshot = Number(line.purchasePrice);
      const unitCostHt = snapshot > 0 ? snapshot : netCostByArticle.get(line.articleId);
      const { benefit: margin } = calculateSalesLineBenefit({
        quantity: line.quantity,
        unitPriceHt: line.unitPriceHt,
        discountPct: line.discountPct,
        unitCostHt,
      });
      return margin > 0 ? sum + (margin * commissionRate) / 100 : sum;
    }, 0);
    const totalUsed = Number(commande.total_used ?? 0);
    const totalAPayer = Math.max(0, doc.totalTtc + doc.deliveryFee - totalUsed);

    const { error: updateError } = await (this.supabase as any)
      .from('pro_commandes')
      .update({
        pht: doc.totalHt,
        pttc: doc.totalTtc,
        tva: doc.totalVat,
        total_commission: round3(totalCommission),
        delivery_fee: doc.deliveryFee,
        total_a_payer: round3(totalAPayer),
        notes: doc.notes,
        delivery_sales_document_id: doc.id,
      })
      .eq('code', doc.proCommandeCode);
    if (updateError) throw updateError;

    const { error: deleteError } = await (this.supabase as any)
      .from('pro_commande_lines')
      .delete()
      .eq('commande_code', doc.proCommandeCode);
    if (deleteError) throw deleteError;

    if (lines.length > 0) {
      const { error: insertError } = await (this.supabase as any)
        .from('pro_commande_lines')
        .insert(lines.map((line, idx) => {
          const grossHt = line.unitPriceHt * line.quantity;
          return {
            tenant_id: commande.tenant_id,
            commande_code: doc.proCommandeCode,
            article_id: line.articleId,
            quantite: line.quantity,
            pht: line.totalHt,
            pttc: line.totalTtc,
            tva: line.vatRate,
            remise: line.discountPct,
            montant_remise: round3(Math.max(0, grossHt - line.totalHt)),
            line_number: idx + 1,
          };
        }));
      if (insertError) throw insertError;
    }
  }
}

function round3(value: number): number {
  return Math.round((value + Number.EPSILON) * 1000) / 1000;
}

/**
 * Coût unitaire net HT (snapshot) à figer sur chaque ligne au moment de
 * l'enregistrement.
 *
 * - Une ligne qui porte déjà un snapshot (> 0) le conserve → le bénéfice est
 *   GELÉ à la date de vente, il ne bougera pas si le prix d'achat de l'article
 *   change ensuite.
 * - Sinon on complète depuis le catalogue, net de la remise fournisseur, pour
 *   que le bénéfice soit correct quel que soit le client qui a créé le document
 *   (web, mobile, POS…). Le calcul du coût net vit dans @erp/domain/sales.
 */
async function resolveNetUnitCosts(
  supabase: TypedSupabaseClient,
  lines: ReadonlyArray<{ articleId?: string | null; purchasePrice?: number | null }>,
): Promise<number[]> {
  const needArticleIds = [...new Set(
    lines
      .filter((l) => l.articleId && !(Number(l.purchasePrice) > 0))
      .map((l) => l.articleId as string),
  )];

  const netByArticle = new Map<string, number>();
  if (needArticleIds.length > 0) {
    const { data, error } = await (supabase as any)
      .from('articles')
      .select('id, purchase_price_ht, supplier_discount')
      .in('id', needArticleIds);
    if (error) throw error;
    for (const row of (data ?? []) as Array<Record<string, unknown>>) {
      const net = netUnitCostHt({
        purchasePriceHt: Number(row['purchase_price_ht'] ?? 0),
        supplierDiscount: Number(row['supplier_discount'] ?? 0),
      });
      if (net != null) netByArticle.set(row['id'] as string, net);
    }
  }

  return lines.map((l) => {
    const snapshot = Number(l.purchasePrice);
    if (Number.isFinite(snapshot) && snapshot > 0) return round3(snapshot);
    if (l.articleId) return netByArticle.get(l.articleId) ?? 0;
    return 0;
  });
}

function escapeLike(value: string): string {
  return value.replace(/[%_]/g, '\\$&');
}

function applySalesDocumentFilters(query: any, filters?: SalesDocumentFilters): any {
  if (filters?.docType) query = query.eq('doc_type', filters.docType);
  if (filters?.search?.trim()) query = query.ilike('number', `%${escapeLike(filters.search.trim())}%`);
  if (filters?.clientId) query = query.eq('client_id', filters.clientId);
  if (filters?.representativeId) query = query.eq('representative_id', filters.representativeId);
  if (filters?.dateFrom) query = query.gte('doc_date', filters.dateFrom);
  if (filters?.dateTo) query = query.lte('doc_date', filters.dateTo);
  if (filters?.isInvoiced !== undefined) query = query.eq('is_invoiced', filters.isInvoiced);
  return query;
}

function mapSalesDocument(row: Record<string, unknown>): SalesDocument {
  const lines = (row['sales_document_lines'] as Record<string, unknown>[] | undefined) ?? [];
  return {
    id: row['id'] as string,
    docType: row['doc_type'] as SalesDocument['docType'],
    number: row['number'] as string,
    clientId: (row['client_id'] as string) ?? '',
    customerSource: (row['customer_source'] as SalesDocument['customerSource']) ?? inferCustomerSource(row),
    onlineCustomerId: (row['online_customer_id'] as string) ?? null,
    proCustomerId: (row['pro_customer_id'] as string) ?? null,
    proClientId: (row['pro_client_id'] as string) ?? null,
    channel: (row['channel'] as string) ?? 'local',
    representativeId: (row['representative_id'] as string) ?? null,
    carRegistrationNumber: (row['car_registration_number'] as string) ?? null,
    docDate: row['doc_date'] as string,
    validityDate: (row['validity_date'] as string) ?? null,
    parentDocId: (row['parent_doc_id'] as string) ?? null,
    parentDocIds: (row['parent_doc_ids'] as string[]) ?? [],
    isInvoiced: (row['is_invoiced'] as boolean) ?? false,
    storefrontOrderStatus: (row['storefront_order_status'] as SalesDocument['storefrontOrderStatus']) ?? null,
    proCommandeCode: (row['pro_commande_code'] as string) ?? null,
    totalHt: (row['total_ht'] as number) ?? 0,
    totalDiscount: (row['total_discount'] as number) ?? 0,
    totalVat: (row['total_vat'] as number) ?? 0,
    totalTtc: (row['total_ttc'] as number) ?? 0,
    withholdingAmount: (row['withholding_amount'] as number) ?? 0,
    stampAmount: (row['stamp_amount'] as number) ?? 0,
    deliveryFee: Number(row['delivery_fee'] ?? 0),
    netToPay: (row['net_to_pay'] as number) ?? 0,
    notes: (row['notes'] as string) ?? null,
    createdBy: (row['created_by'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
    lines: lines.map(mapSalesLine),
    client: row['client'] ? mapClient(row['client'] as Record<string, unknown>) : undefined,
    onlineCustomer: row['online_customer'] ? mapOnlineCustomer(row['online_customer'] as Record<string, unknown>) : null,
    proCustomer: row['pro_customer'] ? mapProCustomer(row['pro_customer'] as Record<string, unknown>) : null,
    proClient: row['pro_client'] ? mapProClient(row['pro_client'] as Record<string, unknown>) : null,
  };
}

function inferCustomerSource(row: Record<string, unknown>): SalesDocument['customerSource'] {
  if (row['pro_customer_id'] || row['pro_commande_code']) return 'pro';
  if (row['online_customer_id']) return 'online';
  return 'local';
}

function mapClient(row: Record<string, unknown>): SalesDocument['client'] {
  return {
    id: row['id'] as string,
    code: row['code'] as string,
    name: row['name'] as string,
    type: row['type'] as 'company' | 'individual',
    taxId: (row['tax_id'] as string) ?? null,
    address: (row['address'] as string) ?? null,
    city: (row['city'] as string) ?? null,
    phone: (row['phone'] as string) ?? null,
    fax: (row['fax'] as string) ?? null,
    email: (row['email'] as string) ?? null,
    defaultDiscountPct: Number(row['default_discount_pct'] ?? 0),
    paymentTermsDays: Number(row['payment_terms_days'] ?? 0),
    notes: (row['notes'] as string) ?? null,
    isDefault: Boolean(row['is_default']),
    carRegistrationNumber: (row['car_registration_number'] as string) ?? null,
    proClientId: (row['pro_client_id'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

function mapOnlineCustomer(row: Record<string, unknown>): NonNullable<SalesDocument['onlineCustomer']> {
  return {
    id: row['id'] as string,
    phone: row['phone'] as string,
    email: (row['email'] as string) ?? null,
    firstName: (row['first_name'] as string) ?? null,
    lastName: (row['last_name'] as string) ?? null,
    fullName: (row['full_name'] as string) ?? null,
    city: (row['city'] as string) ?? null,
    address: (row['address'] as string) ?? null,
    primaryCity: (row['primary_city'] as string) ?? null,
    preferredLanguage: (row['preferred_language'] as string) ?? 'fr',
    totalOrders: Number(row['total_orders'] ?? 0),
    totalSpent: Number(row['total_spent'] ?? 0),
    firstOrderAt: (row['first_order_at'] as string) ?? null,
    lastOrderAt: (row['last_order_at'] as string) ?? null,
    isBlocked: Boolean(row['is_blocked']),
    blockReason: (row['block_reason'] as string) ?? null,
    blockedAt: (row['blocked_at'] as string) ?? null,
    linkedCustomerId: (row['linked_customer_id'] as string) ?? null,
    notes: (row['notes'] as string) ?? null,
    firstUtmSource: (row['first_utm_source'] as string) ?? null,
    firstUtmCampaign: (row['first_utm_campaign'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

function mapProCustomer(row: Record<string, unknown>): NonNullable<SalesDocument['proCustomer']> {
  return {
    id: row['id'] as string,
    tenantId: row['tenant_id'] as string,
    userId: row['user_id'] as string,
    firstName: row['first_name'] as string,
    lastName: row['last_name'] as string,
    email: row['email'] as string,
    phone: (row['phone'] as string) ?? null,
    whatsapp: (row['whatsapp'] as string) ?? null,
    countryCode: (row['country_code'] as string) ?? 'TN',
    status: row['status'] as NonNullable<SalesDocument['proCustomer']>['status'],
    profilePictureUrl: (row['profile_picture_url'] as string) ?? null,
    registrationData: (row['registration_data'] as Record<string, unknown>) ?? null,
    commissionRate: Number(row['commission_rate'] ?? 0),
    defaultRemiseTier:
      row['default_remise_tier'] === 'min' || row['default_remise_tier'] === 'max'
        ? row['default_remise_tier']
        : 'moy',
    approvedAt: (row['approved_at'] as string) ?? null,
    approvedBy: (row['approved_by'] as string) ?? null,
    rejectedReason: (row['rejected_reason'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

function mapProClient(row: Record<string, unknown>): NonNullable<SalesDocument['proClient']> {
  return {
    id: row['id'] as string,
    tenantId: row['tenant_id'] as string,
    proCustomerId: row['pro_customer_id'] as string,
    nom: row['nom'] as string,
    prenom: (row['prenom'] as string) ?? null,
    email: (row['email'] as string) ?? null,
    telephone: (row['telephone'] as string) ?? null,
    adresse: (row['adresse'] as string) ?? null,
    ville: (row['ville'] as string) ?? null,
    cp: (row['cp'] as string) ?? null,
    complement: (row['complement'] as string) ?? null,
    depense: Number(row['depense'] ?? 0),
    commission: Number(row['commission'] ?? 0),
    soldeCumule: Number(row['solde_cumule'] ?? 0),
    notes: (row['notes'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

function mapSalesLine(row: Record<string, unknown>): SalesDocumentLine {
  return {
    id: row['id'] as string,
    documentId: row['document_id'] as string,
    lineNumber: row['line_number'] as number,
    articleId: (row['article_id'] as string) ?? null,
    description: row['description'] as string,
    quantity: row['quantity'] as number,
    unitPriceHt: row['unit_price_ht'] as number,
    purchasePrice: (row['purchase_price'] as number) ?? 0,
    discountPct: (row['discount_pct'] as number) ?? 0,
    vatRate: row['vat_rate'] as number,
    totalHt: row['total_ht'] as number,
    totalTtc: row['total_ttc'] as number,
  };
}
